import { useState, useEffect } from 'react'
import { useAppDispatch } from '../../store/hooks'
import { createMedication } from '../../store/medicationListSlice'
import { X, ChevronDown } from 'lucide-react'

interface AddMedicationModalProps {
  open: boolean
  onClose: () => void
}

const categoryOptions = [
  'Antibiotic',
  'Anti-inflammatory',
  'Analgesic',
  'Sedative',
  'Vaccine',
  'Parasiticide',
  'Hormone',
  'Other',
] as const

const formOptions = [
  'Tablet',
  'Capsule',
  'Liquid',
  'Injectable',
  'Topical',
  'Chewable',
] as const

const frequencyOptions = ['SID', 'BID', 'TID', 'QID', 'q8h', 'q12h', 'PRN'] as const

const deaScheduleOptions = ['II', 'III', 'IV', 'V'] as const

export function AddMedicationModal({ open, onClose }: AddMedicationModalProps) {
  const dispatch = useAppDispatch()

  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
  const [form, setForm] = useState('')
  const [showFormDropdown, setShowFormDropdown] = useState(false)
  const [concentration, setConcentration] = useState('')
  const [defaultDoseRate, setDefaultDoseRate] = useState('')
  const [commonFrequency, setCommonFrequency] = useState('')
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false)
  const [stockQuantity, setStockQuantity] = useState('')
  const [reorderThreshold, setReorderThreshold] = useState('')
  const [controlledSubstance, setControlledSubstance] = useState(false)
  const [deaSchedule, setDeaSchedule] = useState('')
  const [showDeaDropdown, setShowDeaDropdown] = useState(false)
  const [knownInteractions, setKnownInteractions] = useState('')
  const [contraindicatedSpecies, setContraindicatedSpecies] = useState('')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setName('')
      setCategory('')
      setShowCategoryDropdown(false)
      setForm('')
      setShowFormDropdown(false)
      setConcentration('')
      setDefaultDoseRate('')
      setCommonFrequency('')
      setShowFrequencyDropdown(false)
      setStockQuantity('')
      setReorderThreshold('')
      setControlledSubstance(false)
      setDeaSchedule('')
      setShowDeaDropdown(false)
      setKnownInteractions('')
      setContraindicatedSpecies('')
      setNotes('')
      setErrors([])
      setSaving(false)
    }
  }, [open])

  const closeAllDropdowns = () => {
    setShowCategoryDropdown(false)
    setShowFormDropdown(false)
    setShowFrequencyDropdown(false)
    setShowDeaDropdown(false)
  }

  const validate = (): string[] => {
    const errs: string[] = []
    if (!name.trim()) errs.push('Medication Name is required')
    if (!category) errs.push('Category is required')
    if (!form) errs.push('Form is required')
    if (!concentration.trim()) errs.push('Concentration is required')
    return errs
  }

  const handleSave = async () => {
    const errs = validate()
    if (errs.length > 0) {
      setErrors(errs)
      return
    }
    setErrors([])
    setSaving(true)

    try {
      await dispatch(
        createMedication({
          name: name.trim(),
          category,
          form,
          concentration: concentration.trim(),
          default_dose_rate: defaultDoseRate ? parseFloat(defaultDoseRate) : null,
          common_frequency: commonFrequency || 'SID',
          stock_quantity: stockQuantity ? parseInt(stockQuantity, 10) : 0,
          reorder_threshold: reorderThreshold ? parseInt(reorderThreshold, 10) : 0,
          controlled_substance: controlledSubstance,
          dea_schedule: controlledSubstance && deaSchedule ? deaSchedule : null,
          known_interactions: knownInteractions,
          contraindicated_species: contraindicatedSpecies,
          notes,
        })
      ).unwrap()
      onClose()
    } catch {
      setErrors(['Failed to create medication. Please try again.'])
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div
      data-testid="add-medication-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        data-testid="add-medication-modal"
        className="bg-surface rounded-[8px] w-full max-w-lg max-sm:max-w-[calc(100%-24px)] max-h-[85vh] overflow-y-auto"
        style={{ boxShadow: 'var(--shadow-elevation-2)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-[14px] font-semibold text-text-primary">Add Medication</h2>
          <button
            data-testid="add-medication-modal-close"
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 rounded-[4px] text-text-muted hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 px-5 py-4">
          {/* Medication Name */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Medication Name <span className="text-status-error">*</span>
            </label>
            <input
              data-testid="add-medication-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter medication name..."
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Category <span className="text-status-error">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                data-testid="add-medication-category"
                onClick={() => {
                  closeAllDropdowns()
                  setShowCategoryDropdown(!showCategoryDropdown)
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer flex items-center justify-between"
              >
                {category || 'Select category...'}
                <ChevronDown size={12} className="text-text-muted" />
              </button>
              {showCategoryDropdown && (
                <div
                  data-testid="add-medication-category-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-48 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {categoryOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`add-category-option-${opt}`}
                      onClick={() => {
                        setCategory(opt)
                        setShowCategoryDropdown(false)
                      }}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Form <span className="text-status-error">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                data-testid="add-medication-form"
                onClick={() => {
                  closeAllDropdowns()
                  setShowFormDropdown(!showFormDropdown)
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer flex items-center justify-between"
              >
                {form || 'Select form...'}
                <ChevronDown size={12} className="text-text-muted" />
              </button>
              {showFormDropdown && (
                <div
                  data-testid="add-medication-form-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {formOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`add-form-option-${opt}`}
                      onClick={() => {
                        setForm(opt)
                        setShowFormDropdown(false)
                      }}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Concentration */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Concentration <span className="text-status-error">*</span>
            </label>
            <input
              data-testid="add-medication-concentration"
              type="text"
              value={concentration}
              onChange={(e) => setConcentration(e.target.value)}
              placeholder="e.g., 250mg, 10mg/mL"
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Default Dose Rate */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Default Dose Rate (mg/kg)</label>
            <input
              data-testid="add-medication-dose-rate"
              type="number"
              value={defaultDoseRate}
              onChange={(e) => setDefaultDoseRate(e.target.value)}
              placeholder="e.g., 10"
              className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Common Frequency */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Common Frequency</label>
            <div className="relative">
              <button
                type="button"
                data-testid="add-medication-frequency"
                onClick={() => {
                  closeAllDropdowns()
                  setShowFrequencyDropdown(!showFrequencyDropdown)
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer flex items-center justify-between"
              >
                {commonFrequency || 'Select frequency...'}
                <ChevronDown size={12} className="text-text-muted" />
              </button>
              {showFrequencyDropdown && (
                <div
                  data-testid="add-medication-frequency-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {frequencyOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`add-frequency-option-${opt}`}
                      onClick={() => {
                        setCommonFrequency(opt)
                        setShowFrequencyDropdown(false)
                      }}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Stock Quantity and Reorder Threshold in a row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-text-muted">Stock Quantity</label>
              <input
                data-testid="add-medication-stock-quantity"
                type="number"
                value={stockQuantity}
                onChange={(e) => setStockQuantity(e.target.value)}
                placeholder="0"
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-text-muted">Reorder Threshold</label>
              <input
                data-testid="add-medication-reorder-threshold"
                type="number"
                value={reorderThreshold}
                onChange={(e) => setReorderThreshold(e.target.value)}
                placeholder="0"
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
          </div>

          {/* Controlled Substance */}
          <div className="flex items-center gap-2">
            <input
              data-testid="add-medication-controlled"
              type="checkbox"
              checked={controlledSubstance}
              onChange={(e) => {
                setControlledSubstance(e.target.checked)
                if (!e.target.checked) {
                  setDeaSchedule('')
                }
              }}
              className="w-4 h-4 rounded border-border accent-accent"
            />
            <label className="text-[13px] text-text-primary">Controlled Substance</label>
          </div>

          {/* DEA Schedule (only if controlled) */}
          {controlledSubstance && (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-text-muted">DEA Schedule</label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="add-medication-dea-schedule"
                  onClick={() => {
                    closeAllDropdowns()
                    setShowDeaDropdown(!showDeaDropdown)
                  }}
                  className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer flex items-center justify-between"
                >
                  {deaSchedule || 'Select schedule...'}
                  <ChevronDown size={12} className="text-text-muted" />
                </button>
                {showDeaDropdown && (
                  <div
                    data-testid="add-medication-dea-dropdown"
                    className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                    style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                  >
                    {deaScheduleOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        data-testid={`add-dea-option-${opt}`}
                        onClick={() => {
                          setDeaSchedule(opt)
                          setShowDeaDropdown(false)
                        }}
                        className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Known Interactions */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Known Interactions</label>
            <textarea
              data-testid="add-medication-interactions"
              value={knownInteractions}
              onChange={(e) => setKnownInteractions(e.target.value)}
              placeholder="Comma-separated drug names..."
              rows={2}
              className="px-2 py-1 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 resize-none"
            />
          </div>

          {/* Contraindicated Species */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Contraindicated Species</label>
            <textarea
              data-testid="add-medication-contraindicated-species"
              value={contraindicatedSpecies}
              onChange={(e) => setContraindicatedSpecies(e.target.value)}
              placeholder="Comma-separated species..."
              rows={2}
              className="px-2 py-1 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 resize-none"
            />
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Notes</label>
            <textarea
              data-testid="add-medication-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
              className="px-2 py-1 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 resize-none"
            />
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div data-testid="add-medication-errors" className="text-[11px] text-status-error">
              {errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            data-testid="add-medication-cancel"
            onClick={onClose}
            className="inline-flex items-center h-7 px-3 rounded-[4px] text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            Cancel
          </button>
          <button
            data-testid="add-medication-save"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
